import type { SqlSchemaTable } from "../../lib/board/sqlRunner";

/**
 * Схема базы как диаграмма: таблицы прямоугольниками, связи REFERENCES — линиями
 * от столбца-ссылки к первичному ключу. Рисуется из того же описания схемы,
 * что и боковая панель, поэтому работает и для «своей базы».
 */

const XHTML_NS = "http://www.w3.org/1999/xhtml";
const SVG_NS = "http://www.w3.org/2000/svg";

const BOX_W = 208;
const HEAD_H = 28;
const ROW_H = 20;
const BOX_PAD_B = 6;
const GAP_X = 96;
const GAP_Y = 48;
const PAD = 16;

type Placed = {
  table: SqlSchemaTable;
  x: number;
  y: number;
  w: number;
  h: number;
  /** y центра строки столбца, по индексу. */
  rowY: number[];
};

function boxHeight(t: SqlSchemaTable): number {
  return HEAD_H + t.columns.length * ROW_H + BOX_PAD_B;
}

function columnsForCount(n: number): number {
  if (n <= 1) return 1;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  return 4;
}

/** Сетка: колонки одинаковой ширины, строки по самой высокой таблице. */
function layout(tables: SqlSchemaTable[]): { boxes: Placed[]; width: number; height: number } {
  const cols = columnsForCount(tables.length);
  const boxes: Placed[] = [];
  let y = PAD;
  let maxRight = 0;

  for (let i = 0; i < tables.length; i += cols) {
    const row = tables.slice(i, i + cols);
    let rowH = 0;
    row.forEach((t, j) => {
      const h = boxHeight(t);
      const x = PAD + j * (BOX_W + GAP_X);
      const rowY: number[] = [];
      for (let k = 0; k < t.columns.length; k++) {
        rowY.push(y + HEAD_H + k * ROW_H + ROW_H / 2);
      }
      boxes.push({ table: t, x, y, w: BOX_W, h, rowY });
      rowH = Math.max(rowH, h);
      maxRight = Math.max(maxRight, x + BOX_W);
    });
    y += rowH + GAP_Y;
  }

  return { boxes, width: maxRight + PAD, height: y - GAP_Y + PAD };
}

/** Ортогональная линия со скруглениями — как в редакторах схем. */
function connectorPath(x1: number, y1: number, x2: number, y2: number, mx: number): string {
  const r = 8;
  const dyAbs = Math.abs(y2 - y1);
  if (dyAbs < 2) return `M ${x1} ${y1} L ${x2} ${y2}`;
  const sy = y2 > y1 ? 1 : -1;
  const s1 = mx > x1 ? 1 : -1;
  const s2 = x2 > mx ? 1 : -1;
  if (dyAbs < 2 * r || Math.abs(mx - x1) < r || Math.abs(x2 - mx) < r) {
    return `M ${x1} ${y1} L ${mx} ${y1} L ${mx} ${y2} L ${x2} ${y2}`;
  }
  return [
    `M ${x1} ${y1}`,
    `L ${mx - s1 * r} ${y1}`,
    `Q ${mx} ${y1} ${mx} ${y1 + sy * r}`,
    `L ${mx} ${y2 - sy * r}`,
    `Q ${mx} ${y2} ${mx + s2 * r} ${y2}`,
    `L ${x2} ${y2}`,
  ].join(" ");
}

type Link = { path: string; from: [number, number]; to: [number, number] };

function buildLinks(boxes: Placed[]): Link[] {
  const byName = new Map<string, Placed>();
  for (const b of boxes) byName.set(b.table.name.toLowerCase(), b);
  const links: Link[] = [];

  for (const child of boxes) {
    for (const fk of child.table.fks ?? []) {
      const parent = byName.get(fk.table.toLowerCase());
      if (!parent || parent === child) continue;

      const ci = child.table.columns.findIndex(
        (c) => c.name.toLowerCase() === fk.from.toLowerCase()
      );
      const pi = fk.to
        ? parent.table.columns.findIndex((c) => c.name.toLowerCase() === fk.to!.toLowerCase())
        : parent.table.columns.findIndex((c) => c.pk);

      const cy = ci >= 0 ? child.rowY[ci] : child.y + child.h / 2;
      const py = pi >= 0 ? parent.rowY[pi] : parent.y + parent.h / 2;

      let x1: number;
      let x2: number;
      let mx: number;
      if (parent.x + parent.w <= child.x) {
        // родитель слева: выходим из левой грани ребёнка в правую грань родителя
        x1 = child.x;
        x2 = parent.x + parent.w;
        mx = (x1 + x2) / 2;
      } else if (child.x + child.w <= parent.x) {
        x1 = child.x + child.w;
        x2 = parent.x;
        mx = (x1 + x2) / 2;
      } else {
        // таблицы одна над другой — обводим слева
        x1 = child.x;
        x2 = parent.x;
        mx = Math.min(child.x, parent.x) - GAP_X / 2;
      }

      links.push({ path: connectorPath(x1, cy, x2, py, mx), from: [x1, cy], to: [x2, py] });
    }
  }
  return links;
}

export const SQL_ERD_CSS = `
.wb-sql-erd{position:absolute;inset:0;z-index:6;display:flex;flex-direction:column;background:#0b1220;border-radius:12px}
.wb-sql-erd[hidden]{display:none}
.wb-sql-erd__bar{display:flex;align-items:center;gap:8px;padding:8px;flex-shrink:0;border-bottom:1px solid #1e293b}
.wb-sql-erd__title{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#94a3b8}
.wb-sql-erd__hint{font-size:11px;color:#64748b}
.wb-sql-erd__spacer{flex:1 1 auto}
.wb-sql-erd__scroll{flex:1 1 auto;min-height:0;overflow:auto;overscroll-behavior:contain;position:relative}
.wb-sql-erd__canvas{position:relative}
.wb-sql-erd__lines{position:absolute;left:0;top:0;overflow:visible;pointer-events:none}
.wb-sql-erd__table{position:absolute;box-sizing:border-box;border:1px solid #334155;border-radius:9px;background:#0f172a;box-shadow:0 6px 18px rgba(2,6,23,.5);overflow:hidden}
.wb-sql-erd__head{box-sizing:border-box;height:28px;display:flex;align-items:center;justify-content:space-between;gap:6px;padding:0 9px;background:#111c33;border-bottom:1px solid #334155;cursor:pointer}
.wb-sql-erd__head:hover{background:#16233d}
.wb-sql-erd__name{font-size:12px;font-weight:700;color:#5eead4;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.wb-sql-erd__count{font-size:10px;font-weight:600;color:#64748b;flex-shrink:0}
.wb-sql-erd__row{box-sizing:border-box;height:20px;display:flex;align-items:center;gap:6px;padding:0 9px;font-family:ui-monospace,Consolas,monospace;font-size:11px;color:#cbd5e1}
.wb-sql-erd__row--pk{color:#fbbf24}
.wb-sql-erd__row--fk{color:#93c5fd}
.wb-sql-erd__col{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.wb-sql-erd__type{margin-left:auto;font-size:10px;color:#64748b;flex-shrink:0}
.wb-sql-erd__empty{padding:16px;font-size:12px;color:#94a3b8}
`;

export type SqlErdOpts = {
  /** Клик по названию таблицы — показать её содержимое. */
  onPickTable?: (name: string) => void;
};

export function renderSqlErd(
  host: HTMLElement,
  tables: SqlSchemaTable[],
  opts: SqlErdOpts = {}
): void {
  host.textContent = "";
  if (!tables.length) {
    const empty = document.createElementNS(XHTML_NS, "div") as HTMLDivElement;
    empty.setAttribute("class", "wb-sql-erd__empty");
    empty.textContent = "В базе пока нет таблиц.";
    host.appendChild(empty);
    return;
  }

  const { boxes, width, height } = layout(tables);
  const links = buildLinks(boxes);

  const canvas = document.createElementNS(XHTML_NS, "div") as HTMLDivElement;
  canvas.setAttribute("class", "wb-sql-erd__canvas");
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "wb-sql-erd__lines");
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  for (const link of links) {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", link.path);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "#475569");
    path.setAttribute("stroke-width", "1.5");
    svg.appendChild(path);
    for (const [cx, cy] of [link.from, link.to]) {
      const dot = document.createElementNS(SVG_NS, "circle");
      dot.setAttribute("cx", String(cx));
      dot.setAttribute("cy", String(cy));
      dot.setAttribute("r", "3");
      dot.setAttribute("fill", "#64748b");
      svg.appendChild(dot);
    }
  }
  canvas.appendChild(svg);

  const fkColumns = new Map<string, Set<string>>();
  for (const b of boxes) {
    const set = new Set<string>();
    for (const fk of b.table.fks ?? []) set.add(fk.from.toLowerCase());
    fkColumns.set(b.table.name, set);
  }

  for (const b of boxes) {
    const box = document.createElementNS(XHTML_NS, "div") as HTMLDivElement;
    box.setAttribute("class", "wb-sql-erd__table");
    box.style.left = `${b.x}px`;
    box.style.top = `${b.y}px`;
    box.style.width = `${b.w}px`;
    box.style.height = `${b.h}px`;

    const head = document.createElementNS(XHTML_NS, "div") as HTMLDivElement;
    head.setAttribute("class", "wb-sql-erd__head");
    head.title = `Показать содержимое таблицы ${b.table.name}`;
    const name = document.createElementNS(XHTML_NS, "span") as HTMLSpanElement;
    name.setAttribute("class", "wb-sql-erd__name");
    name.textContent = b.table.name;
    const count = document.createElementNS(XHTML_NS, "span") as HTMLSpanElement;
    count.setAttribute("class", "wb-sql-erd__count");
    count.textContent = `${b.table.rows}`;
    head.appendChild(name);
    head.appendChild(count);
    head.addEventListener("click", (ev) => {
      ev.stopPropagation();
      opts.onPickTable?.(b.table.name);
    });
    box.appendChild(head);

    const fks = fkColumns.get(b.table.name) ?? new Set<string>();
    for (const c of b.table.columns) {
      const row = document.createElementNS(XHTML_NS, "div") as HTMLDivElement;
      const isFk = fks.has(c.name.toLowerCase());
      row.setAttribute(
        "class",
        "wb-sql-erd__row" + (c.pk ? " wb-sql-erd__row--pk" : isFk ? " wb-sql-erd__row--fk" : "")
      );
      const label = document.createElementNS(XHTML_NS, "span") as HTMLSpanElement;
      label.setAttribute("class", "wb-sql-erd__col");
      label.textContent = (c.pk ? "🔑 " : "") + c.name;
      const type = document.createElementNS(XHTML_NS, "span") as HTMLSpanElement;
      type.setAttribute("class", "wb-sql-erd__type");
      type.textContent = (c.type || "").toUpperCase();
      row.appendChild(label);
      row.appendChild(type);
      box.appendChild(row);
    }
    canvas.appendChild(box);
  }

  host.appendChild(canvas);
}
